
import axios from 'axios';
import { token_keys, routes } from '../constants';

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/v1`,
});


const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem(token_keys.refresh_token);

  // No refresh token (e.g. on the login screen) → nothing to refresh.
  if (!refreshToken) {
    return false;
  }

  try {
    const body = new URLSearchParams({
      client_id: process.env.REACT_APP_CLIENT_ID,
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    });

    const response = await axiosInstance.post('/accounts/o/token/', body, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    const data = response.data;

    if (data.access_token) {
      localStorage.setItem(token_keys.access_token, data.access_token);
      // ROTATE_REFRESH_TOKEN is enabled on the backend: every refresh mints a NEW
      // refresh token and invalidates the old one, so we must persist it or the
      // next refresh fails. (Both tokens now live ~15 min.)
      if (data.refresh_token) {
        localStorage.setItem(token_keys.refresh_token, data.refresh_token);
      }
      // Reset the expiry clock so session-timeout logic tracks the fresh tokens.
      // This is the reactive (401-interceptor) refresh path, which — unlike the
      // proactive callers — is otherwise the one place that left these stale.
      const expiresIn = data.expires_in || 900;
      localStorage.setItem(token_keys.expiration, expiresIn.toString());
      localStorage.setItem(token_keys.issued_at, Date.now().toString());
      return response; // Return the full response object
    } else {
      throw new Error('Failed to refresh token: No access token in response');
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
    await new Promise((resolve) => {
      setTimeout(() => {
        localStorage.removeItem(token_keys.access_token);
        localStorage.removeItem(token_keys.refresh_token);
        window.location.href = routes.login;
        resolve();
      }, 2000);
    });
    return false;
  }
};


axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Never try to refresh when the failing request IS the token endpoint
    // (e.g. a failed login or a failed refresh), and only attempt a refresh
    // when we actually have a refresh token to send.
    const isTokenRequest = originalRequest?.url?.includes('/accounts/o/token/');
    const hasRefreshToken = Boolean(localStorage.getItem(token_keys.refresh_token));

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isTokenRequest &&
      hasRefreshToken
    ) {
      originalRequest._retry = true;

      try {
        const refreshResponse = await refreshAccessToken();
        if (refreshResponse && refreshResponse.data.access_token) {
          const newAccessToken = localStorage.getItem(token_keys.access_token);
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export { axiosInstance, refreshAccessToken };
